/*
 * Decléor — reassurance slider (product page)
 * ---------------------------------------------------------------------------
 * Drives the dots and the autoplay of the "Livraison gratuite / à partir de 50€" slider.
 *
 * THE TRACK IS A NATIVE SCROLL-SNAP CONTAINER, not a JS carousel. That choice does the heavy
 * lifting for us:
 *   - swipe, momentum and the per-slide stop come from the browser on touch devices,
 *   - it degrades to a plain readable list if this file never loads,
 *   - and Lenis leaves it alone: `allowNestedScroll: true` (assets/smooth-scroll.js) hands the
 *     wheel back to any nested scroller that can still scroll, so a trackpad gesture over the
 *     track does not get eaten by the page.
 * All this file adds is the active-dot state, click-to-slide, and the timer.
 *
 * Autoplay is suspended while the pointer is over the slider or while it has keyboard focus, and
 * it never starts at all under prefers-reduced-motion — an unrequested loop of moving text is the
 * clearest possible case for honouring that.
 *
 * No-op on any page without a [data-reassurance].
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setup(root) {
    var track = root.querySelector('[data-reassurance-track]');
    var dots = root.querySelectorAll('[data-reassurance-dot]');
    if (!track || dots.length < 2) {
      return; // one slide needs no dots and no timer
    }

    var interval = parseInt(root.getAttribute('data-interval'), 10);
    if (isNaN(interval) || interval < 1000) {
      interval = 5000;
    }
    var timer = null;
    var ticking = false;

    function current() {
      // Round rather than floor: mid-scroll the nearest slide is the honest answer.
      return Math.round(track.scrollLeft / (track.clientWidth || 1));
    }

    function paint() {
      var i = current();
      var n;
      for (n = 0; n < dots.length; n += 1) {
        dots[n].classList.toggle('is-active', n === i);
        dots[n].setAttribute('aria-current', n === i ? 'true' : 'false');
      }
    }

    function go(i) {
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
    }

    /* THE AUTOPLAY BOUNCES, IT DOES NOT REWIND.
     * `(current() + 1) % dots.length` looks like the obvious loop and it does loop — measured
     * 0 → 1 → 2 → 0 every 4s. But the wrap is a scroll from the last slide all the way back to the
     * first, and on a native scroll-snap track that is a visible leftward sweep THROUGH every
     * slide in between: logged as 12002ms→slide1, 12251ms→slide0, i.e. the reader watches the
     * whole thing rewind. Cloning slides for a seamless forward wrap is the other fix, but it
     * means duplicated DOM and a scrollLeft reset to hide the seam — a lot of machinery for a
     * three-item text slider.
     * Reversing at the ends keeps every step between adjacent slides, so nothing ever sweeps. */
    var dir = 1;

    function start() {
      if (timer || reduce.matches) {
        return;
      }
      timer = setInterval(function () {
        /* `at`, not `i`: the dot-binding loop below already uses `i` in this same function scope,
         * and a second `var i` here reads as a shadow that isn't one. */
        var at = current();
        /* Turn around at whichever end we have reached. Checked against the CURRENT position each
         * tick rather than counted, so a dot click or a swipe that lands somewhere unexpected
         * cannot leave the direction pointing off the end. */
        if (at + dir > dots.length - 1) {
          dir = -1;
        } else if (at + dir < 0) {
          dir = 1;
        }
        go(at + dir);
      }, interval);
    }
    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    var i;
    for (i = 0; i < dots.length; i += 1) {
      (function (index) {
        dots[index].addEventListener('click', function () {
          stop();
          go(index);
          start();
        });
      })(i);
    }

    track.addEventListener(
      'scroll',
      function () {
        if (ticking) {
          return;
        }
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          paint();
        });
      },
      { passive: true }
    );

    // Suspend while the visitor is reading or tabbing through it.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    paint();
    start();
  }

  function init() {
    var roots = document.querySelectorAll('[data-reassurance]');
    var i;
    for (i = 0; i < roots.length; i += 1) {
      if (!roots[i].dataset.rsBound) {
        roots[i].dataset.rsBound = '1';
        setup(roots[i]);
      }
    }
  }

  init();
  document.addEventListener('shopify:section:load', init);
})();
